import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/context/ToastContext";
import { api, unwrapObject } from "@/lib/api";

const SKIPPED_KEY = "leads.followupSkipped";

/** Leads passed over in this sitting (Next without reaching them), so Next doesn't bring them straight back */
export function followUpSkipped(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem(SKIPPED_KEY) || "[]");
  } catch {
    return [];
  }
}
export function clearFollowUpSkipped() {
  try {
    sessionStorage.removeItem(SKIPPED_KEY);
  } catch {
    /* fine */
  }
}

/**
 * Going through the Follow-up list one by one: how many are left, Contacted and Next. Contacted (or a connected call,
 * a meeting, a message sent) moves the lead to Contacted, so it leaves the list; one passed over waits for the next round.
 */
export function FollowUpBar({ leadId, status, canUpdate, onChanged }: { leadId: string; status: string; canUpdate: boolean; onChanged: () => void }) {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [left, setLeft] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  // New and Follow-up leads are still waiting to be reached
  const waiting = status === "NEW" || status === "FOLLOW_UP";

  useEffect(() => {
    api
      .get(`/api/Lead/FollowUpNext?current=${leadId}`)
      .then((r) => setLeft(unwrapObject<{ left: number }>(r).left))
      .catch(() => setLeft(null));
  }, [leadId]);

  /** "I reached them": the lead moves to Contacted, then on to the next one */
  const contacted = async () => {
    setBusy(true);
    try {
      await api.patch(`/api/Lead/ChangeStatus/${leadId}`, { status: "CONTACTED" });
      success("Marked as contacted", "Moved out of the follow-up list");
      onChanged();
    } catch (e: any) {
      error("Couldn't mark it", e.message || "Please try again");
      setBusy(false);
      return;
    }
    await next();
  };

  const next = async () => {
    setBusy(true);
    try {
      const skipped = followUpSkipped();
      const r = unwrapObject<{ left: number; current_in_list: boolean; next_id: string | null }>(await api.get(`/api/Lead/FollowUpNext?current=${leadId}&skipped=${skipped.join(",")}`));
      const nowSkipped = r.current_in_list && !skipped.includes(leadId) ? [...skipped, leadId] : skipped;
      try {
        sessionStorage.setItem(SKIPPED_KEY, JSON.stringify(nowSkipped.slice(-500)));
      } catch {
        /* fine */
      }
      if (r.next_id) navigate(`/leads/${r.next_id}?followup=1`);
      else {
        success(r.left ? "End of the list" : "All done", r.left ? `${r.left} passed over; start again from the Follow-up list` : "Every lead in Follow-up has been contacted");
        navigate("/leads?tab=followup");
      }
    } catch (e: any) {
      error("Couldn't open the next lead", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-warning/30 bg-warning-soft px-3 py-2 text-[13px]">
      <ListChecks className="size-4 shrink-0 text-warning" />
      <span className="min-w-0 flex-1">
        <strong>Follow-up list</strong>
        {left !== null && <span className="text-muted-foreground"> · {left} left to contact</span>}
        <span className="hidden text-muted-foreground sm:inline"> · reached them? press Contacted</span>
      </span>
      <Link to="/leads?tab=followup" className="text-xs text-muted-foreground hover:text-foreground hover:underline">
        Back to list
      </Link>
      {waiting ? (
        canUpdate && (
          <Button size="sm" className="h-8 gap-1.5" onClick={() => void contacted()} disabled={busy} title="You reached them: move to Contacted and open the next lead">
            <Check className="size-3.5" /> Contacted
          </Button>
        )
      ) : (
        <span className="flex items-center gap-1 text-xs font-medium text-success">
          <Check className="size-3.5" /> Contacted
        </span>
      )}
      <Button size="sm" variant={waiting && canUpdate ? "outline" : "default"} className="h-8 gap-1.5" onClick={() => void next()} disabled={busy} title={waiting ? "Not reached: leave it in the list for later" : undefined}>
        {waiting && canUpdate ? "Skip" : "Next lead"} <ArrowRight className="size-3.5" />
      </Button>
    </div>
  );
}
